<template>
  <div class="inline-block">
    <vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
      <!-- Trigger Button -->
      <vs-button
        type="border"
        color="primary"
        icon-pack="feather"
        icon="icon-eye"
        class="flex items-center"
      >
        <span class="mr-1">{{ label }}</span>
        <span v-if="columns && columns.length" class="text-xs">
          ({{ visibleCount }}/{{ columns.length }})
        </span>
        <feather-icon icon="ChevronDownIcon" class="ml-2 inline-block align-middle h-4 w-4" />
      </vs-button>

      <!-- Dropdown Menu -->
      <vs-dropdown-menu class="w-80 p-0" style="min-width: 300px; max-height: 75vh; display: flex; flex-direction: column;">
        <!-- Header -->
        <div class="px-4 py-3 flex items-center justify-between border-b border-solid d-theme-border-grey-light flex-shrink-0">
          <div class="flex items-center">
            <feather-icon icon="EyeIcon" class="mr-2 text-primary" />
            <h6 class="m-0 font-bold text-sm">Show / Hide Columns</h6>
          </div>
          <vs-chip transparent color="primary" class="m-0 text-xs font-semibold">
            {{ visibleCount }}/{{ columns.length }}
          </vs-chip>
        </div>

        <!-- Column Search -->
        <div class="p-3 border-b border-solid d-theme-border-grey-light flex-shrink-0" @click.stop>
          <vs-input
            icon-pack="feather"
            icon="icon-search"
            placeholder="Search columns..."
            v-model="searchQuery"
            class="w-full"
          />
        </div>

        <!-- Select All Toggle -->
        <div class="px-3 py-2 flex items-center justify-between border-b border-solid d-theme-border-grey-light flex-shrink-0" @click.stop>
          <vs-checkbox
            :value="isAllVisible"
            @input="toggleSelectAll($event)"
            class="m-0 text-xs font-semibold"
          >
            {{ isAllVisible ? 'Hide All' : 'Show All' }}
          </vs-checkbox>
          <small class="font-semibold text-muted">
            {{ visibleCount }} of {{ columns.length }} visible
          </small>
        </div>

        <!-- Column List (Scrollable) -->
        <div class="p-2 overflow-y-auto" style="max-height: 200px;" @click.stop>
          <div
            v-for="col in filteredColumns"
            :key="col.key"
            class="flex items-center justify-between px-2 py-1 my-1 rounded hover:bg-primary-light"
            :class="{ 'opacity-75': col.locked }"
          >
            <!-- Checkbox -->
            <div class="flex items-center flex-1 mr-2 overflow-hidden">
              <vs-checkbox
                :value="col.visible !== false"
                :disabled="col.locked"
                @input="toggleColumn(col.key, $event)"
                class="m-0 text-xs font-medium truncate"
              >
                {{ col.label }}
              </vs-checkbox>
            </div>

            <!-- Sort & Lock Actions -->
            <div class="flex items-center" @click.stop>
              <!-- Active Sort Badge -->
              <button
                v-if="isColumnSorted(col)"
                type="button"
                class="flex items-center px-2 py-1 text-xs font-bold rounded bg-success text-white border-0 cursor-pointer shadow-none whitespace-no-wrap"
                :title="`Current: ${getColumnSortDirection(col)} (Click to switch)`"
                @click.stop="toggleColumnSort(col)"
              >
                <feather-icon
                  :icon="getColumnSortDirection(col) === 'DESC' ? 'ArrowDownIcon' : 'ArrowUpIcon'"
                  svgClasses="mr-1 h-3 w-3 text-white inline-block stroke-current"
                />
                <span class="text-white uppercase font-bold">{{ getColumnSortDirection(col) }}</span>
              </button>

              <!-- Inactive Sort Button -->
              <button
                v-else-if="col.sortKey"
                type="button"
                class="flex items-center px-2 py-1 text-xs font-semibold rounded border border-solid d-theme-border-grey-light hover:border-primary text-grey hover:text-primary bg-transparent cursor-pointer shadow-none whitespace-no-wrap"
                title="Click to sort"
                @click.stop="toggleColumnSort(col)"
              >
                <span>Sort</span>
              </button>

              <!-- Locked Column Indicator -->
              <feather-icon
                v-if="col.locked"
                icon="LockIcon"
                class="ml-2 text-grey h-4 w-4 flex-shrink-0"
                title="Locked column"
              />
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredColumns.length === 0" class="p-4 text-center text-xs text-grey">
            No columns match "{{ searchQuery }}"
          </div>
        </div>

        <!-- Reset Button -->
        <div class="p-3 border-t border-solid d-theme-border-grey-light flex-shrink-0" @click.stop>
          <vs-button
            type="border"
            color="primary"
            class="w-full flex items-center justify-center"
            icon-pack="feather"
            icon="icon-rotate-ccw"
            @click.stop="onReset"
          >
            Reset columns to default
          </vs-button>
        </div>
      </vs-dropdown-menu>
    </vs-dropdown>
  </div>
</template>

<script>
export default {
  name: 'TableColumnPreference',
  props: {
    columns: {
      type: Array,
      required: true,
      default: () => []
    },
    label: {
      type: String,
      default: 'Columns'
    },
    defaultColumns: {
      type: Array,
      default: () => []
    },
    currentSort: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      searchQuery: ''
    }
  },
  computed: {
    // Filter columns based on search input
    filteredColumns () {
      if (!this.searchQuery) return this.columns
      const query = this.searchQuery.toLowerCase().trim()
      return this.columns.filter(col =>
        col.label && col.label.toLowerCase().includes(query)
      )
    },
    visibleCount () {
      return this.columns.filter(c => c.visible !== false).length
    },
    isAllVisible () {
      return this.columns.length > 0 && this.visibleCount === this.columns.length
    }
  },
  methods: {
    // Check if a column matches the currently applied active sort
    isColumnSorted (col) {
      if (!this.currentSort || !col.sortKey) return false
      const activeSortKey = this.currentSort.startsWith('-')
        ? this.currentSort.substring(1)
        : this.currentSort
      return col.sortKey === activeSortKey
    },

    // Return the active sort direction ('ASC' or 'DESC')
    getColumnSortDirection (col) {
      if (!this.isColumnSorted(col)) return ''
      return this.currentSort.startsWith('-') ? 'DESC' : 'ASC'
    },

    // Cycle column sorting: Inactive -> ASC -> DESC -> Neutral
    toggleColumnSort (col) {
      if (!col.sortKey) return

      let nextActive = 'asc'
      if (this.isColumnSorted(col)) {
        const currentDirection = this.getColumnSortDirection(col)
        if (currentDirection === 'ASC') {
          nextActive = 'desc'
        } else if (currentDirection === 'DESC') {
          nextActive = null
        } else {
          nextActive = 'asc'
        }
      }

      this.$emit('sort', { key: col.sortKey, active: nextActive })
    },

    // Toggle visibility for a single column
    toggleColumn (key, isChecked) {
      const updated = this.columns.map(col => {
        if (col.key === key) {
          return {
            ...col,
            visible: col.locked ? true : Boolean(isChecked)
          }
        }
        return col
      })

      this.$emit('update:columns', updated)
      this.$emit('change', updated)
    },

    // Show or hide all unlocked columns
    toggleSelectAll (selectAll) {
      const isVisible = Boolean(selectAll)
      const updated = this.columns.map(col => ({
        ...col,
        visible: col.locked ? true : isVisible
      }))

      this.$emit('update:columns', updated)
      this.$emit('change', updated)
    },

    // Reset column visibility to default
    onReset () {
      const defaults = this.defaultColumns.length ? this.defaultColumns : this.columns
      this.$emit('reset', defaults)
    }
  }
}
</script>
